import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

/*
  FACE ID / TOUCH ID ILE GIRIS
  ----------------------------
  Amac: telefonda her acilista TC ve sifre yazmamak.

  NASIL CALISIR: sifreyi HICBIR YERDE saklamiyoruz. Kullanici bir kez
  normal giris yaptiginda, sunucudan gelen oturum jetonunu telefonun
  kendi korumali alanina yaziyoruz. Uygulama tekrar acildiginda once
  Face ID soruluyor; yuz dogrulanirsa jeton okunup oturum aciliyor.

  NEDEN SIFRE DEGIL JETON: jeton suresi dolabilir ve sunucudan iptal
  edilebilir; sifre edilemez. Telefon calinsa bile ele gecen sey
  sinirli omurlu bir jeton olur.

  KAPALI KALMA HALI: cihazda Face ID tanimli degilse ya da kullanici
  izin vermezse hicbir sey olmaz, klasik sifreli giris akar.
*/

const ANAHTAR_PERSONEL = 'faceIdPersonelJeton';
const ANAHTAR_MUVEKKIL = 'faceIdMuvekkilJeton';
const ANAHTAR_ACIK = 'faceIdAcik';

/*
  Uygulamada miyiz?

  ONEMLI: burada ESKIDEN Capacitor.isPluginAvailable('BiometricAuthNative')
  da kontrol ediliyordu ve Face ID hic acilmiyordu. Uygulama uzaktaki
  siteyi yukledigi icin (server.url) eklenti listesi sayfa acilirken
  her zaman dolu gelmiyor; kontrol false donunce Face ID teklifi hic
  gosterilmiyordu. Artik eklentinin varligi TAHMIN EDILMIYOR: asagida
  gercekten cagrilip cevap verip vermedigine bakiliyor.
*/
const yerli = () => {
  try {
    return typeof Capacitor?.isNativePlatform === 'function'
      && Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};


/*
  Face ID penceresi bazen cevapsiz kalabiliyor (simulator, kullanicinin
  telefonu birakmasi, sistem hatasi). O zaman bekleyen kod sonsuza kadar
  asili kaliyor ve giris tamamlanmiyordu. Bu sarmal en fazla 20 saniye
  bekler, sonra vazgecilmis sayar.
*/
const zamanAsimiyla = (soz, saniye = 20) =>
  Promise.race([
    soz,
    new Promise((_, reddet) =>
      setTimeout(() => reddet(new Error('Face ID zaman asimi')), saniye * 1000)),
  ]);

/*
  EKLENTI ASLA "AWAIT" EDILMEZ -- FACE ID'NIN HIC CALISMAMA SEBEBI.

  Capacitor eklenti nesnesi bir Proxy: bilinmeyen her ozellik icin
  telefona giden bir islev donduruyor -- "then" dahil. Bu yuzden nesne
  JavaScript'e bir SOZ (promise) gibi gorunuyor. Onceden eklenti bir
  async fonksiyondan donduruluyor ve "await" ediliyordu; JavaScript de
  nesnenin then'ini cagiriyor, Capacitor bunu telefona "then komutunu
  calistir" diye iletiyor, telefon boyle bir komut bilmedigi icin hic
  cevap vermiyordu. Sonuc: Face ID kontrolu, acma ve giris ucu de
  sonsuza kadar bekliyordu (eski "eklenti yuklenemedi" mesaji buydu).
  Kayitta da kanitlandi: kontrol sonucunu sunucuya yazan satir hic
  gelmedi.

  Kural: eklenti nesnesini async fonksiyondan DONDURME, await ETME.
  Yalnizca metotlarini (checkBiometry, authenticate) await et.
*/
const eklenti = () => BiometricAuth;

/**
 * Cihaz Face ID / Touch ID destekliyor ve tanimli mi?
 *
 * Eklentinin yuklu olup olmadigi da burada anlasiliyor: yuklu degilse
 * import ya da checkBiometry hata veriyor ve false donuyoruz.
 * Son cagrinin sonucu asagidaki faceIdNedenYok'ta tutuluyor; boylece
 * "Face ID neden cikmadi?" sorusunu tahmin etmeden cevaplayabiliyoruz.
 */
let sonDurum = 'henüz bakılmadı';
export const faceIdNedenYok = () => sonDurum;

export const faceIdVarMi = async () => {
  if (!yerli()) { sonDurum = 'tarayıcıda (uygulama değil)'; return false; }
  try {
    const eklentiNesnesi = eklenti();
    if (!eklentiNesnesi) { sonDurum = 'eklenti bulunamadı'; return false; }

    // checkBiometry bazen hic cevap vermiyor (hata da atmiyor); sure
    // siniri olmadan kart "Kontrol ediliyor…" halinde takiliyordu.
    const durum = await zamanAsimiyla(eklentiNesnesi.checkBiometry(), 8)
      .catch((e) => ({ hata: e }));

    if (durum?.hata) {
      // Gercek hatayi yaz. Tahmin etmek yerine kullanicinin okudugu
      // cumleden sebebi anlayabilelim.
      sonDurum = String(durum.hata?.message || 'telefon yanıt vermedi').slice(0, 70);
      return false;
    }
    if (durum?.isAvailable) { sonDurum = 'hazır'; return true; }

    // Cihazda Face ID kapali/tanimsizsa eklenti sebebini soyluyor.
    sonDurum = durum?.reason
      ? String(durum.reason).slice(0, 70)
      : 'cihazda Face ID tanımlı değil';
    return false;
  } catch (e) {
    sonDurum = String(e?.message || 'eklenti yanıt vermedi').slice(0, 70);
    return false;
  }
};

/** Kullanici bu telefonda Face ID girisini acmis mi? */
export const faceIdAcikMi = () => {
  try {
    return localStorage.getItem(ANAHTAR_ACIK) === '1';
  } catch (e) {
    return false;
  }
};

/*
  SAKLANAN SEY OTURUM DEGIL, FACE ID ANAHTARI.

  Eskiden burada telefondaki oturum jetonu saklaniyordu. Hesap ayni anda
  en fazla 2 cihazda acik kalabildigi icin (muvekkil 1) bilgisayardan ya
  da baska bir cihazdan giris yapilinca telefondaki oturum dusuyor,
  saklanan jeton da onunla gecersizlesiyordu. Face ID yuzu dogruluyor ama
  jeton reddediliyor, giris ekranina geri donulup Face ID yeniden
  soruluyordu: "okuma basarili ama acmiyor", ucuncu denemede sifre.

  Simdi sunucudan bu cihaza ozel bir ANAHTAR aliniyor. Anahtar kendi
  basina hicbir sayfayi acmaz; yalnizca Face ID girisinde YENI bir oturum
  acmaya yarar. Diger cihazlardaki girisler onu bozmaz.
*/
const ANAHTAR_PERSONEL_FID = 'faceIdPersonelAnahtar';
const ANAHTAR_MUVEKKIL_FID = 'faceIdMuvekkilAnahtar';

/*
  GIRIS BILGILERI (kullanici istegi, 2026-10-03)

  Kullanici: "Giris bilgilerini hafizasina kaydetsin. Giriste face id okuyup
  bilgileri doldursun." Face ID acikken kullanici adi ve sifre telefonun
  uygulama alaninda saklaniyor; giris ekraninda yuz okununca kutulara
  doldurulup normal giris yapiliyor. Oturum ya da anahtar ne sebeple
  dusmus olursa olsun (baska cihazdan giris, sure, sunucu) sifreyle giris
  her zaman calisir; yalnizca sifre degisirse bir kez elle girilir ve
  yeni sifre kendiliginden kaydedilir.
*/
const BILGI_PERSONEL = 'faceIdPersonelBilgi';
const BILGI_MUVEKKIL = 'faceIdMuvekkilBilgi';

// Son basarili sifreli giris (yalnizca bellekte). Face ID ayni oturumda
// sonradan acilirsa (giris sonrasi teklif karti) buradan kaydedilir.
const sonGiris = { personel: null, muvekkil: null };

const bilgiYaz = async (tur, bilgi) => {
  const { Preferences } = await tercihModulu();
  await Preferences.set({
    key: tur === 'muvekkil' ? BILGI_MUVEKKIL : BILGI_PERSONEL,
    value: JSON.stringify({ k: bilgi.kullanici, s: bilgi.sifre }),
  });
};

/**
 * Basarili sifreli giristen sonra cagrilir. Face ID aciksa bilgileri
 * kaydeder (sifre degistiyse yenisi yazilir); kapaliysa yalnizca bellekte
 * tutar ki Face ID bu oturumda acilirsa kaydedilebilsin.
 */
export const girisBilgisiniHatirla = async (tur, kullanici, sifre) => {
  if (!kullanici || !sifre) return;
  sonGiris[tur === 'muvekkil' ? 'muvekkil' : 'personel'] = { kullanici, sifre };
  if (!yerli() || !faceIdAcikMi()) return;
  try { await bilgiYaz(tur, { kullanici, sifre }); } catch (e) { /* yoksay */ }
};

/**
 * Bu telefonda Face ID girisi icin kayit var mi? Yalnizca yerel depodaki
 * isarete bakmiyor (o silinebiliyor); telefonun kalici deposuna da bakiyor.
 */
export const faceIdKayitliMi = async () => {
  if (!yerli()) return false;
  if (faceIdAcikMi()) return true;
  try {
    const { Preferences } = await tercihModulu();
    for (const key of [BILGI_PERSONEL, BILGI_MUVEKKIL, ANAHTAR_PERSONEL_FID, ANAHTAR_MUVEKKIL_FID]) {
      if ((await Preferences.get({ key })).value) {
        localStorage.setItem(ANAHTAR_ACIK, '1');
        return true;
      }
    }
  } catch (e) { /* yoksay */ }
  return false;
};

/* DIKKAT: Preferences de bir Capacitor eklentisi (thenable Proxy). Async bir
   fonksiyondan DONDURULURSE await onu soz sanip then'ini cagiriyor ve
   sonsuza kadar bekliyor -- Face ID acarken "Dogrulaniyor..." ekraninda
   takilmanin sebebi buydu. Bu yuzden eklentiyi degil MODULU donduruyoruz;
   kullanan yer { Preferences } diye kendisi aliyor. */
const tercihModulu = () => import('@capacitor/preferences');

/** Sunucudan anahtar alip saklar. Oturum o anda acik ve gecerli olmali. */
const anahtariAlVeSakla = async (tur) => {
  const { authAPI, muvekkilAPI } = await import('../api/api');
  const anahtar = await zamanAsimiyla(tur === 'muvekkil'
    ? muvekkilAPI.faceIdAnahtar()
    : authAPI.faceIdAnahtar(), 15);
  if (!anahtar) return false;
  const { Preferences } = await tercihModulu();
  await Preferences.set({
    key: tur === 'muvekkil' ? ANAHTAR_MUVEKKIL_FID : ANAHTAR_PERSONEL_FID,
    value: anahtar,
  });
  // Eski surumden kalan oturum jetonu artik kullanilmiyor.
  await Preferences.remove({ key: tur === 'muvekkil' ? ANAHTAR_MUVEKKIL : ANAHTAR_PERSONEL });
  return true;
};

/**
 * Face ID girisini acar: yuzu dogrular, bu cihaza ozel anahtari saklar.
 * Ilk parametre eski cagrilarla uyum icin duruyor; oturum, istekler
 * zaten yerel depodaki jetonla gidiyor.
 * @param {string} jeton
 * @param {'personel'|'muvekkil'} tur
 */
export const faceIdAc = async (jeton, tur = 'personel') => {
  if (!yerli() || !jeton) return false;
  try {
    // Once yuzu dogrula ki baskasinin telefonunda acilmasin.
    const BiometricAuth = eklenti();
    await zamanAsimiyla(BiometricAuth.authenticate({
      reason: 'Face ID ile hizli girisi acmak icin kimliginizi dogrulayin',
      cancelTitle: 'Vazgec',
      allowDeviceCredential: false,
      iosFallbackTitle: '',
    }));

    if (!(await anahtariAlVeSakla(tur))) return false;
    localStorage.setItem(ANAHTAR_ACIK, '1');
    const bilgi = sonGiris[tur === 'muvekkil' ? 'muvekkil' : 'personel'];
    if (bilgi) { try { await bilgiYaz(tur, bilgi); } catch (e) { /* yoksay */ } }
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Face ID aciksa anahtari sessizce yeniler (yuz sorulmaz).
 * Gecerli bir oturum her acildiginda cagrilir: eski surumle acilmis
 * telefonlar anahtara kendiliginden gecer, sifre degisikligi sonrasi da
 * anahtar tazelenir. Basarisiz olursa hicbir sey bozulmaz.
 * @param {'personel'|'muvekkil'} tur
 */
export const faceIdAnahtariniTazele = async (tur = 'personel') => {
  if (!yerli() || !faceIdAcikMi()) return;
  try { await anahtariAlVeSakla(tur); } catch (e) { /* yoksay */ }
};

/** Face ID girisini kapatir ve saklanan anahtarlari siler. */
export const faceIdKapat = async () => {
  try {
    localStorage.removeItem(ANAHTAR_ACIK);
    if (!yerli()) return;
    const { Preferences } = await tercihModulu();
    await Preferences.remove({ key: ANAHTAR_PERSONEL });
    await Preferences.remove({ key: ANAHTAR_MUVEKKIL });
    await Preferences.remove({ key: ANAHTAR_PERSONEL_FID });
    await Preferences.remove({ key: ANAHTAR_MUVEKKIL_FID });
    await Preferences.remove({ key: BILGI_PERSONEL });
    await Preferences.remove({ key: BILGI_MUVEKKIL });
  } catch (e) { /* yoksay */ }
};

/**
 * Face ID'yi BIR KEZ sorar; basarili olursa saklanan anahtarlari dondurur.
 * Vazgecilirse ya da yuz dogrulanamazsa null doner.
 * Eskiden personel ve muvekkil icin ayri ayri soruluyordu: personel
 * jetonu reddedilince yuz ikinci kez isteniyordu.
 * @returns {Promise<{personel, muvekkil, personelBilgi, muvekkilBilgi}|null>}
 */
export const faceIdIleGir = async () => {
  if (!yerli() || !(await faceIdKayitliMi())) return null;
  try {
    const BiometricAuth = eklenti();
    await zamanAsimiyla(BiometricAuth.authenticate({
      reason: 'Oturumunuzu acmak icin kimliginizi dogrulayin',
      cancelTitle: 'Sifreyle gir',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Telefon sifresi',
    }));

    const { Preferences } = await tercihModulu();
    const personel = (await Preferences.get({ key: ANAHTAR_PERSONEL_FID })).value || null;
    const muvekkil = (await Preferences.get({ key: ANAHTAR_MUVEKKIL_FID })).value || null;
    const coz = (v) => {
      try { const o = JSON.parse(v); return o && o.k && o.s ? { kullanici: o.k, sifre: o.s } : null; }
      catch (e) { return null; }
    };
    const personelBilgi = coz((await Preferences.get({ key: BILGI_PERSONEL })).value);
    const muvekkilBilgi = coz((await Preferences.get({ key: BILGI_MUVEKKIL })).value);
    return { personel, muvekkil, personelBilgi, muvekkilBilgi };
  } catch (e) {
    return null;
  }
};
